import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { signup } from "../../redux/features/authSlice";
import { setAuthModalOpen } from "../../redux/features/authModalSlice";
import { setUser } from "../../redux/features/userSlice";
import ImageUploadS3 from "./ImageUploadS3";
import userApi from "../../api/user/user.api";
import ProcessingBar from "../../Util/Processingbar";
import Loading from "../../Util/Loading";

const SignupForm = ({ switchAuthState, actionState }) => {
  const dispatch = useDispatch();
  const [isLoginRequest, setIsLoginRequest] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [Images, setImages] = useState([]);

  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState();

  const [isLoading, setIsLoading] = useState(true);

  const getOperators = async () => {
    try {
      const { response, err } = await userApi.getAllOperatorPublic();
      console.log({ response });

      if (response && response.data) {
        setOperators(response.data);
      } else {
        toast.error("No data found");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([getOperators()]);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  console.log({ operators });

  const signupForm = useFormik({
    initialValues: {
      username: "",
      displayName: "",
      password: "",
      confirmPassword: "",
      operator: "",
      photo: "",
      phone: "",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .min(3, "Username must be at least 3 characters")
        .required("Required"),
      displayName: Yup.string()
        .min(3, "Display name must be at least 3 characters")
        .required("Required"),
      operator: Yup.string()
        .min(3, "Operator must be selected")
        .required("Required"),
      phone: Yup.string()
        .min(8, "Phone number must be at least 8 digits")
        .required("Phone number is required"),
      password: Yup.string()
        .min(3, "Password must be at least 3 characters")
        .required("Required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: async (values) => {
      console.log("Form values with photo:", values);

      setErrorMessage("");
      setIsLoginRequest(true);

      try {
        const { response, err } = await userApi.signup(values);

        if (err) {
          throw new Error(err.message || "Sign up failed");
        }

        if (response) {
          signupForm.resetForm();
          dispatch(setUser(response.data));
          dispatch(setAuthModalOpen(false));
          toast.success("Sign up successful");
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
        toast.error(error.message);
      } finally {
        setIsLoginRequest(false);
      }
    },
  });

  const updateImages = (newImageUrl) => {
    setImages([newImageUrl]); // put it inside an array
    signupForm.setFieldValue("photo", newImageUrl || ""); // use the URL string
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <form
      onSubmit={signupForm.handleSubmit}
      className="space-y-4 z-[9999] overflow-y-auto"
    >
      <input
        type="text"
        name="username"
        placeholder="E-mail"
        className="w-full px-4 py-2 border rounded-md"
        onChange={(e) => {
          signupForm.setFieldValue("username", e.target.value.toLowerCase());
        }}
        onBlur={signupForm.handleBlur} // Required for Formik validation to trigger on blur
        value={signupForm.values.username}
      />

      {signupForm.touched.username && signupForm.errors.username && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.username}
        </div>
      )}

      <input
        type="text"
        name="phone"
        placeholder="Phone Number"
        className="w-full px-4 py-2 border rounded-md"
        onChange={signupForm.handleChange}
        value={signupForm.values.phone}
      />

      {signupForm.touched.phone && signupForm.errors.phone && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.phone}
        </div>
      )}

      <input
        type="text"
        name="displayName"
        placeholder="Display Name"
        className="w-full px-4 py-2 border rounded-md"
        onChange={signupForm.handleChange}
        value={signupForm.values.displayName}
      />
      {signupForm.touched.displayName && signupForm.errors.displayName && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.displayName}
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-2">
        {[...operators]
          .filter((op) => op.town !== "KAIKOURA")
          .reverse()
          .map((op) => (
            <label
              key={op._id}
              className={`flex-shrink-0 flex items-center space-x-4 p-4 rounded-lg cursor-pointer transition border ${
                signupForm.values.operator === op._id
                  ? "border-yellow-400 bg-neutral-700"
                  : "border-neutral-700 bg-neutral-800"
              }`}
            >
              <input
                type="radio"
                name="operator"
                value={op._id}
                checked={signupForm.values.operator === op._id}
                onChange={signupForm.handleChange}
                className="accent-yellow-500"
              />
              <div>
                <p className="font-medium text-yellowlight">{op.name}</p>
                <p className="text-sm text-gray-400 whitespace-nowrap">
                  {op.address}
                </p>
              </div>
            </label>
          ))}
      </div>

      {signupForm.touched.operator && signupForm.errors.operator && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.operator}
        </div>
      )}

      <input
        type="password"
        name="password"
        placeholder="Password"
        className="w-full px-4 py-2 border rounded-md"
        onChange={signupForm.handleChange}
        value={signupForm.values.password}
      />

      {signupForm.touched.password && signupForm.errors.password && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.password}
        </div>
      )}

      <input
        type="password"
        name="confirmPassword"
        placeholder="Confirm Password"
        className="w-full px-4 py-2 border rounded-md"
        onChange={signupForm.handleChange}
        value={signupForm.values.confirmPassword}
      />

      {signupForm.touched.confirmPassword &&
        signupForm.errors.confirmPassword && (
          <div className="text-red-500 text-sm mt-1">
            {signupForm.errors.confirmPassword}
          </div>
        )}

      <ImageUploadS3 refreshFunction={updateImages} />

      <button
        type="submit"
        className="w-full text-black hover:text-white py-2 rounded-md transition duration-300"
        disabled={isLoginRequest}
        style={{
          background: "linear-gradient(-45deg, #fed693, #ca7100)",
        }}
      >
        {isLoginRequest ? <ProcessingBar /> : "Sign Up"}
      </button>

      {/* Divider */}
      <div className="flex items-center my-8">
        <div className="flex-grow h-px bg-gray-500"></div>
        <span className="px-4 text-sm text-neutral-300">OR</span>
        <div className="flex-grow h-px bg-gray-500"></div>
      </div>

      <button
        type="button"
        onClick={() => switchAuthState(actionState.signin)}
        className="w-full text-center text-yellow-500 hover:text-white mt-4 py-2 rounded-md border border-yellow-700"
      >
        Sign In
      </button>

      {errorMessage && (
        <p className="text-red-500 text-sm text-center">{errorMessage}</p>
      )}
    </form>
  );
};

export default SignupForm;
